<script setup lang="ts">
import {computed, reactive, watch} from "vue";
import {breakpointsTailwind, useBreakpoints, useStorage} from "@vueuse/core";
import Chart from "primevue/chart";
import ChartDataLabels from "chartjs-plugin-datalabels";
import {Select} from "@/Components/Library";
import {Question} from "@/Models/question";
import Str from "@/Utils/Str.ts";

const breakpoints = useBreakpoints(breakpointsTailwind);
const props = defineProps<{
    question: Question;
    hasFilters?: boolean;
}>();

const isMatrix = computed(() => props.question.param("matrix"));

const chartData = reactive({
    labels: [],
    datasets: [
        {
            label: "Responses",
            data: [],
            backgroundColor: "#44AB89",
        },
    ],
});

const formatLabels = (maxLength = 20) => {
    chartData.labels = props.question.options.map((O) => {
        let str = Str.of(O.value);
        if (breakpoints.smaller("md")) {
            str = str.truncate(28);
            maxLength = 18;
        }
        return str.chunkString(maxLength).toString();
    });
    chartData.datasets[0].backgroundColor = props.question.options.map((O) => O.color);
};

const setMultipleChoiceChartData = () => {
    formatLabels();
    let data: Array<any>;
    if (props.question.params.allow_multiple) {
        data = props.question.responses.map((response: any) => Object.values(response.data)).flat();
    } else {
        data = props.question.responses.map((response: any) => response.data);
    }
    data = data.reduce((acc, curr) => {
        return (acc[curr] ? ++acc[curr] : (acc[curr] = 1), acc);
    }, {});
    chartData.datasets[0].data = props.question.options.map((O) => data[O.id] || 0);
};

const setWeightingChartData = () => {
    formatLabels();
    let data = props.question.responses
        .map((response: any) => response.data)
        .reduce((acc, obj) => {
            Object.keys(obj).forEach((key) => {
                acc[key] = (+acc[key] || 0) + +obj[key];
            });
            return acc;
        }, {});
    chartData.datasets[0].data = Object.values(data);
};
const setRankingChartData = () => {
    formatLabels();
    let scores = props.question.options.map((O) => ({id: O.id, score: 0}));
    let data = props.question.responses.map((response: any) => response.data);
    data.forEach((response: any) => {
        response.forEach((id: any, index: number) => {
            let option = scores.find((O) => O.id === id);
            option.score -= index;
        });
    });
    chartData.datasets[0].data = scores.map((O) => Math.abs(O.score));
};
const setSliderChartData = () => {
    chartData.labels = ["Min", "Median", "Mean", "Max"];
    chartData.datasets[0].backgroundColor = ["#44AB66", "#44AB88", "#44ABDD", "#4461FF"];
    let data = props.question.responses.map((response: any) => response.data);
    if (props.question.params.slider_range) {
        data = props.question.responses.map((response: any) => Object.values(response.data)).flat();
    }
    data.sort((a, b) => a - b);
    const min = Math.min(...data);
    const max = Math.max(...data);
    const mean = parseFloat((data.reduce((acc, curr) => +acc + +curr, 0) / data.length).toFixed(2));
    const median = data[Math.floor(data.length / 2)];
    chartData.datasets[0].data = [min, median, mean, max];
};

const setChartData = () => {
    if (isMatrix.value) {
        return;
    }
    switch (props.question.type.slug) {
        case "multiple_choice":
        case "gatekeeper":
            setMultipleChoiceChartData();
            break;
        case "ranking":
            setRankingChartData();
            break;
        case "weighting":
            setWeightingChartData();
            break;
        case "slider":
            setSliderChartData();
            break;
        default:
            break;
    }
};
setChartData();
watch(() => props.question.responses, setChartData, {deep: true});

const chartType = useStorage("chartType", "bar");
const chartOptions = reactive({
    responsive: true,
    // aspectRatio: 1,
    scales: {
        x: {
            display: chartType.value === "bar",
            grid: {
                display: false, //chartType.value === "bar",
            },
        },
        y: {
            display: false, //chartType.value === "bar",
            grid: {
                display: false,
            },
        },
    },
    plugins: {
        legend: {
            display: false,
        },
        datalabels: {
            // clamp: true,
            display: "auto",
            anchor: "center",
            align: "center",
            // textShadowBlur: 5,
            textShadowColor: "#000000",
            // offset: -100,
            color: "#FFFFFF",
            font: {
                weight: 600,
                size: 16,
            },
            formatter: (value, context) => {
                if (chartType.value === "pie") {
                    return `${context.chart.data.labels[context.dataIndex]} ${value}`;
                }
                // return `${context.chart.data.labels[context.dataIndex]} ${value}`;
            },
        },
    },
});

const setChartOptions = () => {
    if (chartType.value === "pie") {
        chartOptions.scales.y.display = false;
        chartOptions.scales.x.display = false;
    } else {
        chartOptions.scales.x.display = true;
        chartOptions.scales.y.display = false;
    }
};

watch(chartType, () => {
    setChartOptions();
});
</script>
<template>
    <div>
        <div v-if="isMatrix" class="w-full p-8">
            <p class="text-center text-stone-500">Charts are not available for matrix questions.</p>
        </div>
        <div v-else-if="!question.responses.length" class="w-full p-8">
            <p class="text-center text-stone-500">
                {{ hasFilters ? "No responses found for these filters." : "No responses yet." }}
            </p>
        </div>
        <div v-else>
            <div class="mb-6 flex justify-end">
                <Select
                    v-model="chartType"
                    :options="[
                        {label: 'Bar', value: 'bar'},
                        {label: 'Pie', value: 'pie'},
                    ]"
                    optionLabel="label"
                    optionValue="value"
                />
            </div>
            <Chart :type="chartType" :data="chartData" :options="chartOptions" :plugins="[ChartDataLabels]" />
        </div>
    </div>
</template>
<style scoped></style>
